/**
 * Shared building blocks for the System Settings presentation stories
 * (`Presentation/System Settings/*`). The settings page header, the max-width
 * body column and the "row" card (title + description on the left, a control on
 * the right) are defined once here and reused by the Index, General and
 * Security stories so the chrome stays identical and in one place.
 *
 * Mirrors the app: PageHeader.jsx + BackButton.js (header) and the repeated
 * `Card variant="paper-2"` row pattern from the SystemSettings pages.
 */
import React, { ReactElement, ReactNode } from "react";

import styled from "styled-components";

import { MD, SM } from "./tokens";
import { Card } from "../../components/Card";
import { IconButton } from "../../components/IconButton";
import { ArrowLeftIcon } from "../../components/Icons";
import { Body2, Caption1, Header2, Title2 } from "../../components/Typography";

/* ------------------------------------------------------------------ */
/* Settings page header (mirrors PageHeader.jsx + BackButton.js)       */
/* ------------------------------------------------------------------ */

const StyledSettingsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 12px 0;
  width: 100%;
  height: 40px;
  flex-shrink: 0;

  .header-left {
    display: flex;
    align-items: center;
    width: 100%;
    max-width: 100%;

    @media (min-width: ${SM}) {
      min-width: 0;
    }
  }

  .back-button {
    flex-shrink: 0;
    margin-right: 4px;

    @media (min-width: ${MD}) {
      margin-right: 8px;
    }
  }

  .page-title-desktop {
    display: none;
    margin-right: 8px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    @media (min-width: ${MD}) {
      display: block;
    }
  }

  .page-title-mobile {
    display: block;
    margin-right: 8px;
    font-weight: 300;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (min-width: ${MD}) {
      display: none;
    }
  }

  /* keeps the title clear of the absolutely-positioned top-right controls */
  .page-header-spacer {
    display: none;
    height: 100%;
    width: 208px;
    flex-shrink: 0;

    @media (min-width: ${SM}) {
      display: flex;
    }
  }
`;

export interface SettingsHeaderProps {
  title: string;
  backTitle?: string;
}

export const SettingsHeader = ({
  title,
  backTitle,
}: SettingsHeaderProps): ReactElement => (
  <StyledSettingsHeader>
    <div className="header-left">
      {backTitle ? (
        <IconButton variant="text gray" className="back-button">
          <ArrowLeftIcon />
        </IconButton>
      ) : null}
      <Title2 className="page-title-desktop">{title}</Title2>
      <Header2 className="page-title-mobile">{title}</Header2>
    </div>
    <div className="page-header-spacer" />
  </StyledSettingsHeader>
);

/* ------------------------------------------------------------------ */
/* Shared layout pieces                                                */
/* ------------------------------------------------------------------ */

/** The page body, max-width 850px and left-aligned (mirrors the app Page). */
export const SettingsContent = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
  max-width: 850px;
`;

/** A settings row card: title + description on the left, control on the right. */
export const StyledSettingRow = styled(Card)<{ $clickable?: boolean }>`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 68px;
  padding: 12px 16px 12px 20px;
  margin-bottom: 4px;

  ${({ $clickable }) =>
    $clickable
      ? `
    cursor: pointer;

    &:hover {
      background-color: var(--color-theme-200);
    }
  `
      : ""}
`;

export const RowText = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0%;
  min-width: 0;
  padding-right: 12px;

  .row-title {
    margin-bottom: 2px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .row-description {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  /* Long descriptions that are allowed to wrap onto multiple lines. */
  .row-description-wrap {
    white-space: normal;
  }
`;

export interface SettingRowProps {
  title: string;
  description?: ReactNode;
  control?: ReactNode;
  onClick?: () => void;
  /** Let a long description wrap onto multiple lines instead of truncating. */
  wrapDescription?: boolean;
}

export const SettingRow = ({
  title,
  description,
  control,
  onClick,
  wrapDescription = false,
}: SettingRowProps): ReactElement => (
  <StyledSettingRow
    variant="paper-2"
    $clickable={Boolean(onClick)}
    onClick={onClick}
  >
    <RowText>
      <Body2 className="row-title">{title}</Body2>
      {description ? (
        <Caption1
          className={
            wrapDescription ? "row-description-wrap" : "row-description"
          }
          color="tertiary"
        >
          {description}
        </Caption1>
      ) : null}
    </RowText>
    {control}
  </StyledSettingRow>
);
